import type { ProcedureType } from '@/types/cgt'

/**
 * How to NAME a run in the Create CAPA picker.
 *
 * "It should be for a transplant ALL the time or for KIT ID or for MES - WO" —
 * so the label leads with whichever handle that kind of run actually carries,
 * and adds the template so two runs of the same kind can be told apart:
 *
 *   WO-26-0043 · Collection      a transplant, by its work order
 *   LOT-000123 · ICK-NEW-004     a kit run, by its lot
 *   MFG-000042 · Processing      an MES order, by its minted number
 *
 * The CAPA table names the same run through its Procedure column, which reads
 * the template directly — a CAPA lives inside the run it is about, so the table
 * has the run in hand and needs no separate label for it.
 */
export interface CapaTargetLike {
  patientMrn: string | null
  workOrderNumber: string | null
  mfgNumber: string | null
  procedureTemplate: { name: string; type?: ProcedureType | null }
}

/** The run's own handle, by kind. Null when that kind of run has none yet. */
export function capaTargetHandle(target: CapaTargetLike): string | null {
  const type = target.procedureTemplate.type ?? 'Transplant'
  if (type === 'KitCreation') return target.patientMrn
  if (type === 'MES') return target.mfgNumber ?? target.workOrderNumber
  // A transplant is identified by its work order; its patient deliberately does
  // NOT appear here — a CAPA is not raised against a person.
  return target.workOrderNumber
}

/**
 * `<handle> · <template>`, or just the template when the run has no handle yet
 * — a transplant created before work orders were minted, say. Never an empty
 * string: an unlabelled option cannot be chosen.
 */
export function capaTargetLabel(target: CapaTargetLike): string {
  const handle = capaTargetHandle(target)
  const name = target.procedureTemplate.name.trim() || 'Untitled procedure'
  return handle ? `${handle} · ${name}` : name
}
